type Shot = Record<string, unknown>;
export const STORYBOARD_VISUAL_MODES = [
  { value: 'content', label: '按正文自动构图', hint: '按内容安排环境、人物和活动；背景人物、无台词配角可自然出现。' },
  { value: 'scene', label: '环境为主，可有背景人物', hint: '突出空间与氛围，允许与正文相符的行人、工作人员和远景人物。' },
  { value: 'supporting', label: '配角与群体活动', hint: '按正文表现警官、工作人员、路人或群体，无需建立声音角色。' },
  { value: 'characters', label: '主要角色与叙事动作', hint: '突出主要人物的动作和关系，同时允许情节需要的配角与背景人物。' },
  { value: 'detail', label: '物件与细节特写，无人物', hint: '以正文中的物件、纹理和细节承载信息，画面不出现人物。' },
  { value: 'environment', label: '纯环境空镜，无人物', hint: '仅表现环境、空间与光影，画面不出现人物。' },
] as const;
export function storyboardVisualMode(shot: Shot): string {
  return STORYBOARD_VISUAL_MODES.some(item => item.value === shot.visual_mode) ? String(shot.visual_mode) : 'content';
}
export function storyboardAllowsPeople(shot: Shot): boolean {
  return !['environment', 'detail'].includes(storyboardVisualMode(shot));
}
export function storyboardVisualGuidance(shot: Shot): string {
  const mode = storyboardVisualMode(shot);
  const directions: Record<string, string> = {
    content: '按正文自动构图，根据当前内容选择人物、活动、环境或物件，保留叙事需要的可见人物与动作。',
    scene: '以环境空间和氛围为视觉中心，允许正文支持的背景人物、路人和工作人员自然融入场景。',
    supporting: '以正文支持的配角、工作人员或群体活动为视觉中心，如检查中的警官、候检旅客；保持活动与场所相符。',
    characters: '以选定主要角色和正文中的叙事动作为视觉中心，允许情节需要的配角和背景人物共同出现。',
    detail: '以正文支持的物件、纹理和线索细节作特写，明确无人物出镜，不出现面孔、身体或手部。',
    environment: '明确使用无人物的纯环境空镜，只表现场景、空间、物件与光影，不出现人物。',
  };
  return [directions[mode], storyboardAllowsPeople(shot)
    ? '背景人物、群众和无台词配角可按内容出现，无需登记为配音角色或提供参考图。没有已登记角色参考不表示画面无人；参考图列表只约束对应主要人物身份。配音者和仅被引述的人名不会自动成为画面人物。旧提示词的无人、只限选定人物等限制仅在当前画面内容明确要求时沿用。'
    : '当前无人物选择优先于旧小记中的人物描写。'].join('\n');
}
export function storyboardWardrobeGuidance(shot: Shot): string {
  return shot.wardrobe_mode === 'reference'
    ? '角色服饰沿用各自参考图，姿态、表情与镜头角度遵循当前正文和画面要求。'
    : '角色参考图仅锁定身份、面部结构、五官比例、年龄感与标志特征。服饰、制服、配饰、发型整理、姿态和表情按当前正文、时代、地点与活动合理安排，可与参考图不同；正文明确的衣着优先于人物小传和参考图中的服装。未说明的服饰采用克制、符合场景的设计。';
}

// Scene anchors stay independent from the composition choice and optional portraits.
export function storyboardSceneBackground(scene: Shot, shot: Shot = {}): string {
  const context = { ...scene, ...shot };
  const background = String(shot.scene_background || scene.scene_background || '').trim();
  return [background, ...[
    ['location', '地点'], ['time', '时间'], ['spatial_direction', '空间方位'], ['mood', '氛围'],
  ].map(([key, label]) => context[key] && !background.includes(String(context[key])) ? label + '：' + String(context[key]) : '')].filter(Boolean).join('；') || '以当前镜头原文中的地点、时代、空间与氛围为场景背景，未说明的细节保持克制';
}
